import React, { useState, useContext } from 'react';
import styled, { useTheme } from 'styled-components';
import LanguageContext from '@lib/contexts/languageContext';
import { Trans } from '@lingui/react';
import Card from '@organisms/cards/Card';
import ButtonFavoriteVideoCard from '@components/ButtonFavoriteVideoCard';
import Icon from '@components/Icon';
import ButtonAddtoPlaylist from '@ecosystems/ButtonAddtoPlaylist';
import ButtonScheduleModal from '@ecosystems/ButtonScheduleModal';
import GenreInstructorMetadata from '@ecosystems/GenreInstructorMetadata';
import MenuItemShareModal from '@ecosystems/MenuItemShareModal';
import { Instructor, ProgramCategory } from '@gql/generated';
import {
  Popover,
  PopoverContent,
  PopoverItem,
  PopoverTrigger,
} from 'src/ui/popover';
import { Button } from '@components/ButtonV2';

export const StyledCard = styled(Card)`
  padding-left: 0;
  margin-bottom: 20px;
`;

const BottomRightWrapper = styled.span`
  position: absolute;
  right: 6px;
  bottom: 6px;
`;

const DurationWrapper = styled.span`
  background-color: rgba(0, 0, 0, 0.75);
  font-size: 11px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.neutrals.white};
  text-align: center;
  border-radius: 4px;
  padding: 5px 7px;
  display: block;
`;

const CenterWrapper = styled.div`
  position: absolute;
  width: 19px;
  height: 19px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid #fff;
  border-radius: 50%;
  padding: 29px;
  box-shadow: 0;
  transition: all 0.3s;
`;

const ActionButtonWrapper = styled.div`
  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const PlayIconWrapper = styled.div`
  position: absolute;
  top: 55%;
  left: 54%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

export const ThumbnailWrapper = styled.div<{
  locked?: boolean;
  progress: number;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: pointer;
  }

  &:hover > .changeBackgroundOnHover {
    box-shadow: 0px 0px 14px 0px rgba(0, 0, 0, 0.75);
  }

  :after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    width: ${({ progress }) => progress}%;
    background-color: #129edc;
  }
`;

export const VideoTag = styled.span`
  display: flex;
  color: ${(props) => props.theme.colors.neutrals.white};
  border-radius: 3px;
  padding: 5px 10px;
  margin-right: 8px;
  background-color: ${(props) => props.theme.colors.primary.stone};
`;

export const TagsWrapper = styled.div<{ appearance?: ProgramCategory }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: start;
  padding: 10px;

  ${VideoTag} {
    height: 20px;
    box-sizing: content-box;
    background-color: ${({ theme, appearance }) =>
      appearance === ProgramCategory['Challenges']
        ? theme.colors.primary.green
        : theme.colors.primary.stone};
  }
`;

type Video = {
  id: string;
  identifier: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  kind: string;
  flavor: string;
  publishedAt: number;
  duration: number;
  genreName: string;
  genreSlug: string;
  instructors: Instructor[];
  cursorAt: number;
};

type Props = {
  video: Video;
  redirectTo: string;
  locked: boolean;
  tags?: string[];
  onClick?(): void;
  disableActions?: boolean;
  programCategory?: ProgramCategory;
};

const ProgramVideoCard = (props: Props) => {
  const {
    video,
    redirectTo,
    locked,
    tags = [],
    onClick = null,
    disableActions = false,
    programCategory,
  } = props;
  const locale = useContext(LanguageContext);
  const theme = useTheme();
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [rightMenuOpen, setRightMenuOpen] = useState(false);
  const handleOpenMenu = (event) => {
    setAnchorElMenu(event.currentTarget);
    setRightMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setRightMenuOpen(false);
    setAnchorElMenu(null);
  };

  let redirectLink = redirectTo;
  if (!onClick && !redirectTo) {
    redirectLink = `/${locale}/videos/${video.identifier}`;
  }

  let isMobile = false;
  if (typeof window !== 'undefined') {
    isMobile = window.matchMedia(
      `(max-width: ${theme.viewports.laptop}px)`
    ).matches;
  }

  const progress =
    video?.cursorAt > 0
      ? Math.floor(((video?.cursorAt / 60) * 100) / video.duration)
      : 0;

  return (
    <StyledCard
      data-testid="ProgramVideoCard"
      large={!isMobile}
      onClick={onClick}
      redirectTo={redirectLink}
      thumbnailUrl={video.thumbnailUrl}
      thumbnailContent={
        <ThumbnailWrapper locked={locked !== false} progress={progress}>
          {!locked ? (
            <>
              <CenterWrapper className="changeBackgroundOnHover">
                <PlayIconWrapper>
                  <Icon name="ygb-icon-play" color="white" fontSize="17.5px" />
                </PlayIconWrapper>
              </CenterWrapper>
            </>
          ) : null}
          <BottomRightWrapper>
            <DurationWrapper>
              {video.duration} <Trans id="metadata.min" />
            </DurationWrapper>
          </BottomRightWrapper>
          {tags && (
            <TagsWrapper
              appearance={programCategory}
              data-testid="ProgramVideoCard--Tags"
            >
              {video?.kind === 'audio' ? (
                <VideoTag key="-1" data-testid="ProgramVideoCard--Tag">
                  <img width="14px" src="/static/icons/HeadSetIcon.svg" />
                </VideoTag>
              ) : null}
              {locked ? (
                <VideoTag key="lock" data-testid="ProgramVideoCard--Tag">
                  <Icon
                    name="ygb-icon-programlock-ic"
                    color="white"
                    fontSize="17px"
                  />
                </VideoTag>
              ) : null}
              {tags?.map((tag, i) => {
                let content = null;
                if (typeof tag === 'string') {
                  content = <Trans id={tag} />;
                } else {
                  content = tag;
                }
                return (
                  <VideoTag key={i} data-testid="ProgramVideoCard--Tag">
                    {content}
                  </VideoTag>
                );
              })}
            </TagsWrapper>
          )}
        </ThumbnailWrapper>
      }
      contentTitle={video.title}
      contentAuthor={
        <GenreInstructorMetadata
          genre={video.genreName}
          genreSlug={video.genreSlug}
          instructors={video.instructors}
        />
      }
      contentDescription={video.description}
      contentCardLeftSection={
        !disableActions ? (
          <ActionButtonWrapper
            className={locked ? 'disabled' : ''}
            data-testid="ActionButtonWrapper"
          >
            <ButtonFavoriteVideoCard videoId={video.id} />
          </ActionButtonWrapper>
        ) : null
      }
      contentCardRightSection={
        !disableActions ? (
          <React.Fragment>
            <ActionButtonWrapper
              // we use ActionButtonWrapper to make sure what needs to have disable have it during tests
              className={locked ? 'disabled' : ''}
              data-testid="ActionButtonWrapper"
            >
              <Popover>
                <PopoverTrigger>
                  <Icon name={'ygb-icon-menu-hor'} fontSize="5px" />
                </PopoverTrigger>
                <PopoverContent>
                  <ButtonAddtoPlaylist
                    tag={PopoverItem}
                    videoId={video.id}
                    handleCloseParentMenu={handleCloseMenu}
                    anchorElMenu={anchorElMenu}
                    handleOpenParentMenu={() => {
                      handleOpenMenu({
                        currentTarget: anchorElMenu,
                      });
                    }}
                  />
                  <ButtonScheduleModal
                    tag={PopoverItem}
                    resource="video"
                    resourceId={video.id}
                  />
                  <MenuItemShareModal
                    resourceIdentifier={video.identifier}
                    resource="videos"
                  />
                </PopoverContent>
              </Popover>
            </ActionButtonWrapper>
          </React.Fragment>
        ) : null
      }
      contentLeftCardLargeSection={
        !disableActions
          ? [
              <ActionButtonWrapper
                key={1}
                className={locked ? 'disabled' : ''}
                data-testid="ActionButtonWrapper"
              >
                <ButtonFavoriteVideoCard videoId={video.id} />
              </ActionButtonWrapper>,
              <ActionButtonWrapper
                key={2}
                className={locked ? 'disabled' : ''}
                data-testid="ActionButtonWrapper"
              >
                <ButtonAddtoPlaylist
                  tag={Button}
                  tagParams={{
                    link: true,
                    appearance: 'dark',
                    className: 'uppercase !text-xs',
                  }}
                  videoId={video.id}
                  handleCloseParentMenu={handleCloseMenu}
                />
              </ActionButtonWrapper>,
              <ActionButtonWrapper
                key={3}
                className={locked ? 'disabled' : ''}
                data-testid="ActionButtonWrapper"
              >
                <ButtonScheduleModal
                  tag={Button}
                  tagParams={{
                    link: true,
                    appearance: 'dark',
                    className: 'uppercase !text-xs',
                  }}
                  resource="video"
                  resourceId={video.id}
                />
              </ActionButtonWrapper>,
            ]
          : []
      }
      contentRightCardLargeSection={
        !disableActions ? (
          <React.Fragment>
            <ActionButtonWrapper
              className={locked ? 'disabled' : ''}
              data-testid="ActionButtonWrapper"
            >
              <Popover>
                <PopoverTrigger aria-label="Open menu">
                  <Icon name="ygb-icon-menu-hor" fontSize="5px" />
                </PopoverTrigger>
                <PopoverContent>
                  <MenuItemShareModal
                    resourceIdentifier={video.identifier}
                    resource="videos"
                  />
                </PopoverContent>
              </Popover>
            </ActionButtonWrapper>
          </React.Fragment>
        ) : null
      }
    />
  );
};

export default ProgramVideoCard;
